import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { interval, merge, of, shareReplay, take, timer } from 'rxjs';
import { transition, trigger, useAnimation } from '@angular/animations';
import { fadeIn, fadeOut } from 'ng-animate';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ActivitySectionDirective,
  HappyHoursPeriodNextModel,
  HappyHoursPeriodNowModel,
  HappyHoursService,
  UserActivityClickTrackingDirective,
} from '@core';

interface HappyHoursTimerStatus {
  now: boolean;
  show: boolean;
  active: boolean;
  timeLeft: string;
  activeCountdownTime: string;
}

@Component({
  selector: 'app-happy-hours',
  templateUrl: './happy-hours.component.html',
  imports: [
    RouterLink,
    ActivitySectionDirective,
    UserActivityClickTrackingDirective,
    TranslatePipe,
  ],
  styleUrls: ['./happy-hours.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        useAnimation(fadeIn, {
          params: { timing: 0.3 },
        }),
      ]),
      transition(':leave', [
        useAnimation(fadeOut, {
          params: { timing: 0.3 },
        }),
      ]),
    ]),
  ],
})
export class HappyHoursComponent {

  private readonly translate = inject(TranslateService);
  private readonly happyHoursService = inject(HappyHoursService);

  private readonly emptyStatus: HappyHoursTimerStatus = {
    now: false,
    show: false,
    active: false,
    timeLeft: '',
    activeCountdownTime: '',
  };

  public readonly currentLanguage = toSignal(this.translate.onLangChange.pipe(
    map((event) => {
      return event.lang;
    }),
  ), { initialValue: this.translate.getCurrentLang() });

  private readonly status$ = this.happyHoursService.status$.pipe(
    map((hh) => {
      if (!(
        hh instanceof HappyHoursPeriodNowModel
      ) && !(
        hh instanceof HappyHoursPeriodNextModel && hh.timeLeft.asMinutes() <= 30
      )) {
        return {
          now: false,
          show: false,
          active: false,
          timeLeft: '',
          activeCountdownTime: '',
          initialSeconds: 0,
        };
      }

      const isNowHappyHours = hh instanceof HappyHoursPeriodNowModel;
      const initialSeconds = hh.timeLeft.asSeconds();

      return {
        now: isNowHappyHours,
        show: initialSeconds <= 30 * 60,
        active: true,
        timeLeft: '',
        activeCountdownTime: '',
        initialSeconds,
      };
    }),
    shareReplay(1),
  );

  private readonly timer$ = this.status$.pipe(
    switchMap(({ initialSeconds, now, show, active }) => {
      if (!active || initialSeconds <= 0) {
        return of(this.emptyStatus);
      }

      return timer(0, 1000).pipe(
        take(Math.ceil(initialSeconds) + 1),
        map((elapsed) => {
          const countDown = this.formatTimeLeft(initialSeconds - elapsed);

          return {
            now,
            show,
            active,
            timeLeft: countDown,
            activeCountdownTime: countDown,
          };
        }),
      );
    }),
    shareReplay(1),
  );

  private readonly show$ = this.timer$.pipe(
    map((status) => status.active || status.now),
    distinctUntilChanged(),
    switchMap((show) => {
      if (!show) {
        return of(false);
      }

      const period = 600_000;
      const showPeriod = 30_000;

      return merge(
        of(true),
        timer(showPeriod, period).pipe(
          map(() => false),
        ),
        interval(period).pipe(
          map(() => true),
        ),
      );
    }),
  );

  public readonly status = toSignal(this.timer$, {
    initialValue: this.emptyStatus,
  });

  public readonly show = toSignal(this.show$, {
    initialValue: false,
  });

  private formatTimeLeft(secondsLeft: number): string {
    const seconds = Math.max(0, Math.floor(secondsLeft));
    const mm = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const ss = (seconds % 60).toString().padStart(2, '0');

    return `${mm}:${ss}`;
  }

}
